import { type FC, useRef, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FileCard } from "@/shared/components/ui/file-card";
import { FieldError } from "@/shared/components/ui/FieldError";
import { supabase } from "@/shared/lib/supabase";
import { extractTextFromPdf } from "@/shared/utils/pdfTextExtraction";
import { convertImageFileToPdf } from "@/shared/utils/convertImageFileToPdf";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { SecurityNote } from "@/shared/components/ui/security-note";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

const currentYear = new Date().getFullYear();

const TAX_YEARS = [
  { year: currentYear - 1, optional: false },
  { year: currentYear - 2, optional: true },
  { year: currentYear - 3, optional: true },
] as const;

async function uploadSingleFile(
  file: File,
  estateId: number,
  accessToken: string,
): Promise<number> {
  let fileForUpload = file;
  let fileName = file.name;
  let contentType = file.type;

  if (contentType.startsWith("image/")) {
    const converted = await convertImageFileToPdf(file);
    fileForUpload = converted;
    fileName = converted.name;
    contentType = "application/pdf";
  }

  const extractedText =
    contentType === "application/pdf" ? await extractTextFromPdf(fileForUpload) : undefined;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  const presignedRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/presigned-url`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ file_name: fileName }),
    },
  );
  if (!presignedRes.ok) throw new Error("Failed to get upload URL");
  const { upload_url, file_key } = await presignedRes.json();

  const uploadRes = await fetch(upload_url, {
    method: "PUT",
    body: fileForUpload,
    headers: { "Content-Type": contentType },
  });
  if (!uploadRes.ok) throw new Error("Failed to upload file");

  const metadataRes = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/metadata`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        file_key,
        file_name: fileName,
        document_type: "tax_document",
        extracted_text: extractedText,
      }),
    },
  );
  if (!metadataRes.ok) throw new Error("Failed to save document");
  const { document } = await metadataRes.json();
  return document.id as number;
}

export const TaxReturnUpload: FC<FlowCustomComponentProps> = ({ onComplete, setAnswer }) => {
  const [files, setFiles] = useState<Record<number, File>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const { person, user } = useAppContext();

  const hasRequiredFile = TAX_YEARS.some(({ year, optional }) => !optional && files[year]);

  const handleFileSelect = (year: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFiles((prev) => ({ ...prev, [year]: selectedFile }));
    }
  };

  const handleRemoveFile = (year: number) => {
    setFiles((prev) => {
      const next = { ...prev };
      delete next[year];
      return next;
    });
    const input = inputRefs.current[year];
    if (input) {
      input.value = "";
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const handleContinue = async () => {
    if (!hasRequiredFile || isUploading) return;

    setIsUploading(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const estateId = person?.estate_id;
      if (!estateId) throw new Error("Person not found");

      const documentIds: number[] = [];
      for (const { year } of TAX_YEARS) {
        const file = files[year];
        if (file) {
          const docId = await uploadSingleFile(file, estateId, session.access_token);
          documentIds.push(docId);
        }
      }

      setAnswer("tax_return_document_ids", documentIds.map(String));
      onComplete();
    } catch (err) {
      setError("Failed to upload tax returns. Please try again.");
      sentryUtils.captureError(
        err,
        "Tax return upload failed",
        { userId: user?.user_id, estateId: person?.estate_id },
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-primary bg-white p-4">
        {TAX_YEARS.map(({ year, optional }, index) => (
          <div key={year}>
            {index > 0 && <hr className="my-4 border-primary" />}
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {year} Tax Return{" "}
                {optional && <span className="font-normal text-tertiary">(optional)</span>}
              </p>
              {!files[year] && (
                <label className="cursor-pointer">
                  <Button
                    variant="outline"
                    onClick={() => inputRefs.current[year]?.click()}
                    disabled={isUploading}
                  >
                    Upload
                  </Button>
                  <input
                    ref={(el) => {
                      inputRefs.current[year] = el;
                    }}
                    type="file"
                    className="hidden"
                    accept={ACCEPTED_TYPES}
                    onChange={(e) => handleFileSelect(year, e)}
                  />
                </label>
              )}
            </div>
            {files[year] && (
              <div className="mt-3">
                <FileCard
                  fileName={files[year].name}
                  fileSize={files[year].size}
                  status="idle"
                  onRemove={() => handleRemoveFile(year)}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <SecurityNote text="Files are encrypted and stored securely in your Documents." />

      {error && <FieldError message={error} className="mt-2" />}

      <div className="mt-7 flex flex-col items-center">
        <Button
          variant="brand"
          rounded={true}
          onClick={handleContinue}
          disabled={!hasRequiredFile || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Continue <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
        <Button
          variant="link"
          className="text-secondary"
          onClick={handleSkip}
          disabled={isUploading}
        >
          Skip
        </Button>
      </div>
    </div>
  );
};
